/* eslint-disable @typescript-eslint/no-explicit-any */
import { CloudLocation } from "@/models/microsoft/CloudLocation";
import { WorkspaceDocument } from "@/models/workspace/Workspace";
import { createContext, useContext } from "react";
import { DownloadedFileWithIds } from "./SharePointProvider";
import { GraphCallOptions } from "@/services/microsoft/graphApiService";
import { CloudFileSyncFetch } from "@/types/sync.types";

import { DownloadedFolderWithIds } from "./SharePointProvider";

export type LaunchPickerOptions = {
  /** Where the picker opens; defaults to the user's OneDrive. */
  location?: CloudLocation;
};

export type PickerMode = "file" | "folder";
export type PickerPurpose = "upload" | "selection";

export type OpenCloudLocationChooserOptions = {
  /** Invoked when the user dismisses the chooser without picking a location. */
  onCancel?: () => void;
};

/** The route a SharePoint download was started from. */
export type DownloadOrigin = {
  workspaceId: string | null;
  pathname: string;
};

/**
 * One SharePoint/OneDrive pick, waiting to be uploaded.
 *
 * Downloads are queued rather than held in a single slot: the provider that
 * owns them sits above the router and a folder of a few hundred documents
 * takes a while, so a user can easily start a second pick before the first has
 * been uploaded. A single slot meant the second silently replaced the first —
 * no error, the toast still reported success, and the first batch was never
 * uploaded.
 */
export type DownloadBatch = {
  id: string;
  /** Where the user was when they picked; the upload goes here, nowhere else. */
  origin: DownloadOrigin;
  files: DownloadedFileWithIds[];
  /**
   * The "upload venter" toast shown when this batch finished downloading while
   * the user was elsewhere, so it can be dismissed once the upload starts.
   */
  pendingToastId?: string | number | null;
};

export type SharePointContextProps = {
  /**
   * Entry point for every "open the Microsoft picker" action: shows the
   * "Vælg placering" chooser and launches the picker from the user's choice.
   * Call it synchronously from the user's click.
   */
  openCloudLocationChooser: (
    e: React.MouseEvent<HTMLElement> | undefined,
    pickerMode: PickerMode,
    purpose: PickerPurpose,
    options?: OpenCloudLocationChooserOptions
  ) => void;
  /**
   * Opens the Microsoft File Picker. Must be called synchronously from the
   * user's click (the picker window needs the click's popup allowance); `e`
   * may be undefined when the click came from a non-mouse control.
   */
  launchPicker: (
    e: React.MouseEvent<HTMLElement> | undefined,
    pickerMode?: "file" | "folder",
    purpose?: "upload" | "selection",
    options?: LaunchPickerOptions
  ) => void;
  /**
   * Reads a linked document's cloud metadata and downloads the content only
   * when `shouldDownload(cloudLastModified)` is true.
   */
  fetchFileForSync: (
    document: WorkspaceDocument,
    shouldDownload: (cloudLastModified: number) => boolean,
    graphOptions?: GraphCallOptions
  ) => Promise<CloudFileSyncFetch>;
  fetchGraphPdfForCloudItem: (ids: {
    driveId: string;
    siteId: string;
    sharepointFileId: string;
  }) => Promise<Blob | null>;
  uploadFileToSharePoint: (
    file: File,
    targetPath?: string
  ) => Promise<any | null>;
  /** Downloads waiting to be uploaded, oldest first. */
  downloadQueue: DownloadBatch[];
  /** Adds a finished download, pinned to where the user picked it. */
  enqueueDownloadBatch: (
    files: DownloadedFileWithIds[],
    origin: DownloadOrigin
  ) => void;
  /** Drops one batch once it has been handed to the upload pipeline. */
  dequeueDownloadBatch: (batchId: string) => void;
  selectedFolder: {
    id: string;
    name: string;
    driveId?: string;
    siteId?: string;
  } | null;
  setSelectedFolder: (
    folder: {
      id: string;
      name: string;
      driveId?: string;
      siteId?: string;
    } | null
  ) => void;
  openSharePointDocumentInWordOffline: (
    driveId: string,
    itemId: string
  ) => Promise<void>;
  openSharePointDocumentInWordOnline: (
    driveId: string,
    itemId: string
  ) => Promise<void>;
  downloadFolderContentsForSync: (
    driveId: string,
    siteId: string,
    folderId: string
  ) => Promise<{
    files: DownloadedFileWithIds[];
    folders: DownloadedFolderWithIds[];
  }>;
};

export const SharePointContext = createContext<SharePointContextProps | null>(
  null
);

export const useSharePointContext = (): SharePointContextProps => {
  const context = useContext(SharePointContext);
  if (!context) {
    throw new Error(
      "useSharePointContext must be used inside a SharePointProvider"
    );
  }
  return context;
};
